import { ApiConfig, HttpClient, HttpResponse, RequestParams, type ContentType } from '../HttpClient'
import * as ApiCore from '../services/ApiCoreService'

const CDA_UID = (typeof window !== 'undefined' && window.env.APP_CDA_UID) || ''
const hostName =
  (typeof window !== 'undefined' && window.location.hostname.replace(/^www./, '')) || ''

const APP_API_URL = 'https://api.' + hostName.split('.').splice(-2).join('.')

const RemapLangDict: Record<string, string> = {
  kz: 'kk',
}

export const mapLanguageToApi = (locale: string): string => RemapLangDict[locale] ?? locale

if (typeof window !== 'undefined')
  window.addEventListener('languageChanged', (event) => {
    const customEvent = event as CustomEvent
    Locale.set(customEvent.detail)
  })

export class Locale {
  private static currentLocale: string = 'ru'

  static get(): string {
    return this.currentLocale
  }

  static set(locale: string): void {
    this.currentLocale = mapLanguageToApi(locale)
    Rest.changeLocale(this.currentLocale)
  }
}

export class ProjectId {
  private static currentProjectId: string = String(CDA_UID)

  static get(): string {
    return this.currentProjectId
  }

  static set(projectId: string | number): void {
    this.currentProjectId = String(projectId)
    Rest.changeHeaders({ 'project-id': this.currentProjectId })
  }
}

type SecurityDataType = { Authorization: string }
type BaseUrlScope = 'core'
type CoreApi = ApiCore.Api<SecurityDataType>['core']
type CdaApi = ApiCore.Api<SecurityDataType>['cda']

abstract class Rest {
  private static coreApi: ApiCore.Api<SecurityDataType>
  private static coreHttpClient: HttpClient<SecurityDataType>
  private static apiRegistry:
    | Partial<Record<BaseUrlScope, HttpClient<SecurityDataType>>>
    | undefined

  private static createConfig(): ApiConfig<SecurityDataType> {
    return {
      baseUrl: APP_API_URL,
      baseApiParams: {
        credentials: 'include',
        headers: {
          'project-id': ProjectId.get(),
          'accept-language': Locale.get(),
          'origin-project-id': String(CDA_UID),
          // 'content-type': 'application/json',
        },
      },
    }
  }

  public static init(): void {
    const coreHttpClient = new HttpClient<SecurityDataType>(this.createConfig())
    const coreApi = new ApiCore.Api<SecurityDataType>(coreHttpClient)

    this.coreHttpClient = coreHttpClient
    this.coreApi = coreApi
    this.apiRegistry = { core: coreHttpClient }
  }

  public static get core(): CoreApi {
    return this.coreApi.core
  }

  public static get cda(): CdaApi {
    return this.coreApi.cda
  }

  public static setBaseUrl(baseUrl: string, scope: BaseUrlScope | true = true): void {
    const registry = this.apiRegistry
    if (!registry) return

    if (scope === true) {
      for (const api of Object.values(registry)) {
        if (api) api.baseUrl = baseUrl
      }
    } else if (registry[scope]) {
      registry[scope].baseUrl = baseUrl
    }
  }

  public static changeHeaders(headers: HeadersInit): void {
    this.coreHttpClient?.changeHeaders(headers)
  }

  public static changeLocale(locale: string): void {
    this.changeHeaders({ 'accept-language': mapLanguageToApi(locale) })
  }
}

Rest.init()

export const getApiError = <TError>(err: unknown): TError => {
  if (err && typeof err === 'object' && 'error' in err) {
    return (err as HttpResponse<unknown, TError>).error
  }

  return err as TError
}

export type { ContentType, HttpResponse, RequestParams }

export { Rest as default }
